import { FieldLabel } from "../workspaces/shared/FieldLabel";
import { useState } from "react";
import { userMessage } from "../workspaces/shared/userMessage";
import { saveProfile, type AccountState, type HostedAccount } from "./session";
import { accessSummary } from "./EarlyAccessScreens";
import { Badge, Callout, PageHeader } from "../workspaces/shared/ui";
import { Check, CreditCard, ShieldCheck } from "lucide-react";

/**
 * YOUR ACCOUNT (Phase 13C, spec §59B).
 *
 * Everything a person needs to do to their own sign-in, without asking the
 * administrator and without anyone touching a server: change the password,
 * see which account they are signed in as, and see which build they are
 * using when they report that something broke.
 *
 * The administrator never sees or sets a tester's password — they can only
 * send the same single-use reset link the tester could request themselves.
 */
export function AccountScreen({
  user,
  version,
  onClose,
  onStateChanged,
  onOpenBilling,
}: {
  user: HostedAccount;
  version: string;
  onClose: () => void;
  /** Spec §86: the profile form saves through the server and reports the new state. */
  onStateChanged?: (state: AccountState) => void;
  onOpenBilling?: () => void;
}) {
  const [profileName, setProfileName] = useState(user.displayName && user.displayName !== user.email ? user.displayName : "");
  const [company, setCompany] = useState(user.profile?.companyName ?? "");
  const [experience, setExperience] = useState<string>(user.profile?.experienceLevel ?? "");
  const [intent, setIntent] = useState<string>(user.profile?.primaryIntent ?? "");
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [current, setCurrent] = useState("");
  const [emailCurrent, setEmailCurrent] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailDone, setEmailDone] = useState<string | null>(null);
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (next !== confirm) {
      setError("Those two passwords are different.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await fetch("/api/account/password", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      const payload = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) throw new Error(payload.error ?? "That password could not be changed.");
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(userMessage(err, "That password could not be changed."));
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    setEmailBusy(true);
    setEmailError(null);
    try {
      const response = await fetch("/api/account/email", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword: emailCurrent, email: newEmail }),
      });
      const payload = (await response.json().catch(() => ({}))) as { error?: string; email?: string };
      if (!response.ok) throw new Error(payload.error ?? "That address could not be changed.");
      // The change ends every session for this account, including this one.
      setEmailDone(payload.email ?? newEmail);
    } catch (err) {
      setEmailError(userMessage(err, "That address could not be changed."));
    } finally {
      setEmailBusy(false);
    }
  };

  if (emailDone) {
    return (
      <div className="panel settings-confirm">
        <h2>Your sign-in address is now {emailDone}</h2>
        <p className="workspace-note">
          Your password, your projects and everything else are unchanged. You have been signed out everywhere — sign
          back in with the new address.
        </p>
        <button className="btn btn-primary" onClick={() => window.location.assign("/")}>
          Sign in
        </button>
      </div>
    );
  }

  const summary = user.access ? accessSummary(user.access as AccountState["access"]) : null;
  const displayName = user.displayName && user.displayName !== user.email ? user.displayName : "";
  const initials = (displayName || user.email).split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");

  return (
    <div className="settings-page">
      <PageHeader
        title="Account"
        lede="Your profile, sign-in and access."
        actions={
          <button className="btn btn-ghost" onClick={onClose}>
            Back to Berrier Reno
          </button>
        }
      />
      <div className="settings-grid">
        <div className="settings-main">
          {user.profile && onStateChanged && (
            <section className="panel settings-section">
              <div className="settings-section-head">
                <h2>Profile</h2>
                <p className="muted">How you appear, and the defaults new projects start with.</p>
              </div>
              <form
                className="settings-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  setProfileBusy(true);
                  setProfileMessage(null);
                  void saveProfile({ displayName: profileName, companyName: company, experienceLevel: experience, primaryIntent: intent })
                    .then((state) => {
                      // The name is part of what the top bar shows; carry it back with the state.
                      onStateChanged({ ...state, displayName: profileName.trim() || user.email } as AccountState);
                      setProfileMessage("Saved.");
                    })
                    .catch((err: unknown) => setProfileMessage(userMessage(err, "That could not be saved.")))
                    .finally(() => setProfileBusy(false));
                }}
              >
                <div className="form-row-2">
                  <FieldLabel>
                    Name
                    <input value={profileName} onChange={(e) => setProfileName(e.target.value)} autoComplete="name" />
                  </FieldLabel>
                  <FieldLabel>
                    <span>
                      Company <span className="auth-hint">optional</span>
                    </span>
                    <input value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="organization" />
                  </FieldLabel>
                </div>
                <div className="form-row-2">
                  <FieldLabel>
                    Renovation experience
                    <select value={experience} onChange={(e) => setExperience(e.target.value)}>
                      <option value="">Not said</option>
                      <option value="new">New to renovation</option>
                      <option value="some">Some experience</option>
                      <option value="experienced">Experienced</option>
                    </select>
                  </FieldLabel>
                  <FieldLabel>
                    Mostly used for
                    <select value={intent} onChange={(e) => setIntent(e.target.value)}>
                      <option value="">Not said</option>
                      <option value="flip">Flips</option>
                      <option value="rental">Rentals</option>
                      <option value="other">Other renovation</option>
                    </select>
                    <span className="auth-hint">Only the default for new projects — nothing is restricted.</span>
                  </FieldLabel>
                </div>
                <div className="form-actions">
                  <button className="btn btn-primary" type="submit" disabled={profileBusy}>
                    {profileBusy ? "Saving…" : "Save profile"}
                  </button>
                  {profileMessage && (
                    <span className={profileMessage === "Saved." ? "save-note save-note-good" : "save-note"} role="status">
                      {profileMessage === "Saved." && <Check size={14} aria-hidden="true" />} {profileMessage}
                    </span>
                  )}
                </div>
              </form>
            </section>
          )}

          <details className="panel settings-section collapsible-panel">
            <summary>Change password</summary>
            <div className="settings-section-head">
              <h2>Password</h2>
              <p className="muted">Changing it signs you out of every other browser.</p>
            </div>
            <form className="settings-form" onSubmit={(event) => void submit(event)}>
              {done && <Callout tone="good">Your password has been changed. Any other browser you were signed in on has been signed out.</Callout>}
              <FieldLabel>
                Current password
                <input type="password" autoComplete="current-password" value={current} onChange={(event) => setCurrent(event.target.value)} required />
              </FieldLabel>
              <div className="form-row-2">
                <FieldLabel>
                  New password
                  <input type="password" autoComplete="new-password" value={next} onChange={(event) => setNext(event.target.value)} required minLength={8} />
                  <span className="auth-hint">At least 8 characters. A short phrase works well.</span>
                </FieldLabel>
                <FieldLabel>
                  Type the new one again
                  <input type="password" autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} required />
                </FieldLabel>
              </div>
              {error && <Callout tone="bad">{error}</Callout>}
              <div className="form-actions">
                <button className="btn" type="submit" disabled={busy}>
                  {busy ? "Changing…" : "Change password"}
                </button>
              </div>
            </form>
          </details>

          <details className="panel settings-section collapsible-panel">
            <summary>Change sign-in email address</summary>
            <div className="settings-section-head">
              <h2>Sign-in address</h2>
              <p className="muted">Same account, projects and password — the old address stops working and you are signed out everywhere.</p>
            </div>
            <form className="settings-form" onSubmit={(event) => void submitEmail(event)}>
              <div className="form-row-2">
                <FieldLabel>
                  New email address
                  <input type="email" autoComplete="email" value={newEmail} onChange={(event) => setNewEmail(event.target.value)} required />
                </FieldLabel>
                <FieldLabel>
                  Your current password
                  <input type="password" autoComplete="current-password" value={emailCurrent} onChange={(event) => setEmailCurrent(event.target.value)} required />
                </FieldLabel>
              </div>
              {emailError && <Callout tone="bad">{emailError}</Callout>}
              <div className="form-actions">
                <button className="btn" type="submit" disabled={emailBusy}>
                  {emailBusy ? "Changing…" : "Change address"}
                </button>
              </div>
            </form>
          </details>
        </div>

        <aside className="settings-side">
          <section className="panel account-card">
            <span className="avatar avatar-large" aria-hidden="true">
              {initials}
            </span>
            <strong className="account-card-name">{displayName || user.email}</strong>
            <span className="muted">{user.email}</span>
            <Badge tone={user.role === "admin" ? "brand" : "neutral"}>{user.role === "admin" ? "Administrator" : "Member"}</Badge>
          </section>
          {summary && (
            <section className="panel">
              <span className="access-card-label">Access</span>
              <p className="account-access">
                <Badge tone={summary.tone} dot>
                  {summary.label}
                </Badge>
              </p>
              {onOpenBilling && (
                <button className="btn btn-small" onClick={onOpenBilling}>
                  <CreditCard size={14} aria-hidden="true" /> Billing
                </button>
              )}
            </section>
          )}
          <section className="panel account-privacy">
            <ShieldCheck size={18} aria-hidden="true" />
            <p className="muted">Your projects are private to your account. Nobody else can see them unless you share a project, and Berrier Holdings never has your password.</p>
            <span className="muted small">
              Version <code>{version || "unknown"}</code>
            </span>
          </section>
        </aside>
      </div>
    </div>
  );
}
